/**
 * WEB-700 — the EventModule contract and its self-gating selector.
 *
 * The whole event-detail-v2 page composition turns on one rule: a category
 * slug picks an ordered module set (the "lexicon"), and every module decides
 * for itself whether it has anything to show by checking its own backing
 * field on the already-fetched event payload. This file is the single place
 * that rule is stated — nothing downstream re-derives it (WEB-701 onward
 * register renderers against `EventModuleKind` and trust `hasContent`).
 *
 * Pure, synchronous, React-free: no fetching, no env reads, no `Date.now()`,
 * no feature flags. Safe to call from Server Components, Client Components,
 * Storybook, and unit tests alike.
 */

import { publishesOrganizer, resolveAboutColumnBody } from '@/lib/eventDetail/eventSourceCopy';
import { resolveGenericModuleRows } from '@/lib/eventDetail/genericModuleRows';
import { derivePeopleRows } from '@/lib/eventDetail/peopleRows';
import { resolveTicketingPriceNote } from '@/lib/eventDetail/ticketingPriceNote';
import { isUsableHttpUrl } from '@/lib/ticketing';
import type { EventDetailV2 } from '@/types/eventDetail';

export { resolveGenericModuleRows } from '@/lib/eventDetail/genericModuleRows';

/**
 * The closed set of module kinds the event-detail module tier can render.
 * Extending the page composition means adding a kind here first.
 */
export type EventModuleKind =
  | 'about'
  | 'lineup'
  | 'matchup'
  | 'seatTiers'
  | 'works'
  | 'runOfDates'
  | 'menu'
  | 'agenda'
  | 'people'
  | 'audience'
  | 'generic';

/** A lexicon module entry after gating, ready for the template to render or degrade (M4). */
export interface ResolvedEventModule {
  kind: EventModuleKind;
  /** Zero-padded section ordinal ('01', '02', …), assigned by array position after gating. */
  ordinal: string;
  title: string;
  meta: string | null;
  moreHref: string | null;
  moreLabel: string | null;
  /** false => the renderer shows the degrade note instead of rows (M4). */
  hasContent: boolean;
}

/**
 * The minimal per-module wording a lexicon entry must supply for
 * `resolveEventModules` to do its job. WEB-703 owns the real ten-slug
 * lexicon map (`config/eventLexicon.ts`, `Record<CategorySlug,
 * EventLexiconEntry>` with the actual per-category copy); this interface is
 * the smallest structural shape this selector actually reads. Building the
 * lexicon map itself is out of scope here.
 */
export interface EventLexiconModuleEntry {
  kind: EventModuleKind;
  title: string;
  meta?: string | null;
  moreHref?: string | null;
  moreLabel?: string | null;
}

/**
 * The module-tier slice of a lexicon entry: the ordered module set for one
 * category slug, and nothing else.
 *
 * WEB-703's `EventLexiconEntry` **extends** this rather than replacing it, so
 * a full lexicon entry (headings, kickers, CTA verb, breadcrumb label) passes
 * straight into `resolveEventModules` while this selector stays free of any
 * dependency on the copy map — no import cycle, and the module order has
 * exactly one representation.
 */
export interface EventModuleLexicon {
  /** First-to-last section order for this slug. */
  moduleOrder: readonly EventLexiconModuleEntry[];
}

/**
 * Calendar-day key (`yyyy-mm-dd`) for an ISO timestamp in a given IANA
 * timezone. `en-CA` renders ISO-style digits, matching the project's
 * existing tz-bucketing convention (see `lib/home/groupEventsByDay.ts`).
 */
function localDateKey(isoDateTime: string, timeZone: string): string | null {
  const instant = new Date(isoDateTime);
  if (Number.isNaN(instant.getTime())) {
    // Malformed timestamp on the wire. `Intl.DateTimeFormat.format` throws a
    // RangeError on an Invalid Date, and this selector's contract is that it
    // never throws — same defensive posture as `lib/home/groupEventsByDay.ts`.
    return null;
  }
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    // Invalid IANA identifier in `event.timezone` — the Intl constructor
    // throws a RangeError before it ever formats. Unprojectable, not fatal.
    return null;
  }
}

/**
 * `runOfDates` needs `endDate` to land on a different LOCAL calendar day than
 * `date`. Projected through `event.timezone` (server-authoritative, PIP-98)
 * — never the viewer's locale, never `Date.now()`. Falls back to UTC only
 * when the event carries no timezone, so the comparison stays deterministic
 * either way.
 */
function hasTimeComponent(isoDateTime: string): boolean {
  return /\d{2}:\d{2}/.test(isoDateTime);
}

function isMultiDayRun(event: EventDetailV2): boolean {
  if (!event.endDate) {
    return false;
  }
  // API-1410 / API-1614: `startTimeKnown === false` means the ingestor only had
  // a calendar date, so `date` is a FABRICATED local midnight. Browse surfaces
  // exclude those events, but the event-detail endpoint still serves them (with
  // an honest "Time TBA" treatment) — which is exactly this selector's surface.
  // Projecting a fabricated instant into a timezone invents a local day, and
  // comparing it against `endDate` would invent a multi-day run. Same for a
  // date-only string on either endpoint: `new Date('2026-06-12')` parses as UTC
  // midnight per ECMAScript, so projecting it into a western zone silently
  // reports the PREVIOUS day (the PIP-98 drift this module must not reintroduce).
  // Gate false — we cannot prove a run we were never told the time for.
  if (event.startTimeKnown === false) {
    return false;
  }
  if (!hasTimeComponent(event.date) || !hasTimeComponent(event.endDate)) {
    return false;
  }
  const zone = event.timezone ?? 'UTC';
  const startKey = localDateKey(event.date, zone);
  const endKey = localDateKey(event.endDate, zone);
  if (startKey === null || endKey === null) {
    // Cannot project either endpoint (malformed timestamp or bad IANA zone),
    // so we cannot prove a multi-day run. Gate false rather than guess — the
    // renderer shows the degrade note, which is the honest outcome.
    return false;
  }
  return startKey !== endKey;
}

/**
 * The one gating rule, stated once: a module is `hasContent: true` only when
 * its own backing field is non-empty on `event`. Never duplicate this switch
 * elsewhere — every renderer trusts `ResolvedEventModule.hasContent` instead
 * of re-checking the raw event fields itself.
 */
function hasModuleContent(kind: EventModuleKind, event: EventDetailV2): boolean {
  switch (kind) {
    case 'about':
      // FRE-1313 — the event's own body copy. This column is the reason
      // `description` has been on the wire since API-1403: it shipped in every
      // payload and, until this kind existed, no module rendered it.
      //
      // The gate is NOT `description.trim().length > 0`. `description` is not
      // one kind of string — for Ticketmaster rows it is venue policy, which
      // `EventSourceInfoBlocks` already renders as "Event Info" directly above
      // this tier in the same editorial column. `resolveAboutColumnBody` is
      // where that classification lives (WEB-806 owns it); asking it here
      // keeps the source rules in one file instead of two that drift. A
      // whitespace-only body resolves null there, so it degrades rather than
      // painting an empty column.
      return resolveAboutColumnBody(event) !== null;
    case 'lineup':
      return Array.isArray(event.artists) && event.artists.length > 0;
    case 'runOfDates':
      return isMultiDayRun(event);
    case 'seatTiers': {
      // No per-tier LIST on EventDetailV2 today — `meta.priceTierCount` is a
      // COUNT, not rows. Honest content we DO hold: `meta.seatmapUrl`
      // (API-1516 / WEB-711), `meta.ticketLimitInfo` (WEB-806), and the
      // server-assembled price story (WEB-851). Any one opens the slot.
      // Non-http(s) seatmap schemes stay false so a hostile payload cannot
      // open the slot on the URL alone.
      //
      // WEB-851 added the price story because its absence was the bug: an
      // Eventbrite row with a published band and neither seatmap nor ticket
      // limit gated OFF and printed the degrade note — telling the reader we
      // hold no price directly beneath a CTA already showing one.
      const raw = event.meta.seatmapUrl;
      const hasSeatmap = typeof raw === 'string' && isUsableHttpUrl(raw.trim());
      const ticketLimit = event.meta.ticketLimitInfo;
      const hasTicketLimit = typeof ticketLimit === 'string' && ticketLimit.trim().length > 0;
      const hasPriceNote = resolveTicketingPriceNote(event) !== null;
      return hasSeatmap || hasTicketLimit || hasPriceNote;
    }
    case 'people':
      // WEB-854 — row emptiness is this kind's `hasContent`, the same
      // arrangement `generic` has below. `derivePeopleRows` is the single row
      // source, shared with `PeopleModule`, so the gate and the renderer can
      // never disagree about whether there is a host to show.
      //
      // This case USED to sit in the unconditional-false group beneath it,
      // and that was the WEB-854 bug: `EventDetailV2` carried no organizer
      // field, so every Eventbrite community listing rendered an empty
      // "Organizers" module while its own `/e/` page named the host. The
      // field now exists end to end, so the slot opens exactly when the
      // source published someone — and stays shut, with the honest degrade
      // note, when it did not.
      return derivePeopleRows(event).length > 0;
    case 'matchup':
    case 'works':
    case 'menu':
    case 'audience':
      // Awaiting supply: none of these kinds has a backing column on
      // EventDetailV2 today — no home/away record + form, artwork
      // checklist, per-course menu, or audience guidance copy on the wire.
      // Resolves false unconditionally until a future contract change adds
      // the field each one needs.
      return false;
    case 'generic':
    case 'agenda':
      // WEB-756 — row emptiness is this kind's `hasContent`, not a second
      // opinion inside GenericModule. `resolveGenericModuleRows` is the
      // single row source (always empty today); a description on the event
      // is About-column copy, not a pour/agenda list, so it must not open
      // the slot. That About column is now the `about` kind above (FRE-1313),
      // which is where a description belongs — this stays row-only.
      return resolveGenericModuleRows(event).length > 0;
    default: {
      // Exhaustiveness guard: adding a kind to the union without a case
      // above fails type-check here instead of silently gating true.
      const exhaustiveCheck: never = kind;
      return exhaustiveCheck;
    }
  }
}

/**
 * WEB-855 — whether a slot belongs on THIS event's page at all, asked before
 * {@link hasModuleContent} is consulted.
 *
 * The distinction between the two is the whole of this ticket, and it is the
 * one place page composition is allowed to differ by source rather than by
 * category:
 *
 * - **Gating** (`hasContent`) answers *"do we hold the content?"*. A `false`
 *   still occupies its slot and prints the honest degrade note (WEB-719 / M4)
 *   — the absence is a true and useful thing to tell a reader
 *   about a field the source could have published.
 * - **Applicability** (here) answers the prior question *"could this source
 *   ever publish it?"*. A `false` removes the section, because
 *   "Lineup not listed." on a lane with no organizer
 *   concept is not a degrade — it is a permanent, unfillable notice about a
 *   gap that cannot exist.
 *
 * Exactly one kind answers `false` today: `people`, and only on a source that
 * does not publish organizers. WEB-855 orders the slot on all ten lexicons
 * plus the fallback so every Eventbrite event surfaces the host WEB-854 made
 * real; `publishesOrganizer` is what keeps that from also printing a
 * permanent notice beneath every Ticketmaster concert (which names its people
 * in the `lineup` module directly above) and beneath every UNC-Charlotte,
 * CRVA, venue-site, seeded, and hand-created row. That regression on the
 * largest category is precisely why WEB-854 declined to add the slot itself.
 *
 * **The row check comes first, and it is what makes an allowlist safe.** A
 * lane outside the set that nonetheless carries an organizer — a future
 * ingestion lane, or an admin who fills the field on a manual row — keeps its
 * slot and shows the name. So the failure mode of a mis-scoped list is a
 * visible, honest note, never a host we hold and silently refuse to render,
 * which would be the WEB-854 bug in a new costume.
 */
function isModuleSlotApplicable(kind: EventModuleKind, event: EventDetailV2): boolean {
  if (kind !== 'people') {
    return true;
  }
  // Asked through the gate rather than `derivePeopleRows` directly, so "do we
  // hold a host" keeps exactly one definition.
  if (hasModuleContent('people', event)) {
    return true;
  }
  return publishesOrganizer(event);
}

/**
 * Resolves a category slug's lexicon-ordered module set against the
 * already-fetched event payload. Pure and synchronous — the template calls
 * it during render; Storybook and the M4 guard test call it directly with
 * hand-built payloads.
 *
 * Ordinals are assigned by array position AFTER gating, not filtered by
 * `hasContent` — a gated-off module still occupies its slot (the renderer
 * shows a degrade note there instead, M4), so numbering never skips
 * regardless of which fields the event happens to have.
 *
 * WEB-855 — the one exception, and it is not a gating exception: a slot the
 * source could never fill is dropped by {@link isModuleSlotApplicable}
 * BEFORE ordinals are assigned, so the surviving sections still number
 * `01, 02, 03…` with no gap. That ordering is load-bearing; filtering after
 * the map would leave a hole in the sequence a reader can see.
 */
export function resolveEventModules(
  event: EventDetailV2,
  lexicon: EventModuleLexicon,
): ResolvedEventModule[] {
  return lexicon.moduleOrder
    .filter((entry) => isModuleSlotApplicable(entry.kind, event))
    .map((entry, index) => ({
      kind: entry.kind,
      ordinal: String(index + 1).padStart(2, '0'),
      title: entry.title,
      meta: entry.meta ?? null,
      moreHref: entry.moreHref ?? null,
      moreLabel: entry.moreLabel ?? null,
      hasContent: hasModuleContent(entry.kind, event),
    }));
}
